import { Ref, useRef } from "react";
import { CreateOrderWizard, CreateOrderDeliveryStep } from "../helpers/createOrderHelpers";
import { Controller, useForm } from "react-hook-form";
import { Divider, Grid } from "@mantine/core";
import { DeliveryStepInput, DeliveryValidationSchema } from "./Validation/DeliveryValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import TextInput from "@/components/atoms/TextInput";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import Checkbox from "@/components/atoms/Checkbox";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";

interface Props {
  onValidationSuccess: (data: CreateOrderDeliveryStep) => void;
  onChangeWizardData: (data: CreateOrderDeliveryStep) => void;
  wizardData: CreateOrderWizard;
  ref?: Ref<HTMLFormElement>;
}

function Delivery({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const differentAddressNodeRef = useRef(null);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<DeliveryStepInput>({
    mode: "onSubmit",
    resolver: zodResolver(DeliveryValidationSchema),
    defaultValues: { ...wizardData },
  });

  const onSubmit = (data: DeliveryStepInput) => {
    onChangeWizardData(data);
    onValidationSuccess(data);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={6}>
          <Controller
            control={control}
            name="validFrom"
            render={({ field: { onChange, value } }) => (
              <DatePickerInput label={t("common:validFrom")} withAsterisk onChange={onChange} value={value} />
            )}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Controller
            control={control}
            name="validTo"
            render={({ field: { onChange, value } }) => (
              <DatePickerInput label={t("common:validTo")} withAsterisk value={value} onChange={onChange} />
            )}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          {wizardData.orderType === OrderTypeEnum.BUY ? (
            <Controller
              control={control}
              name="lastDeliveryDate"
              render={({ field: { onChange, value } }) => (
                <DatePickerInput label={t("orders:lastDeliveryDate")} value={value} onChange={onChange} />
              )}
            />
          ) : null}
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>

        <Grid.Col span={12}>
          <Controller
            control={control}
            name="differentAddress"
            render={({ field: { onChange, value } }) => (
              <Checkbox
                label={t("contracts:differentAddress")}
                checked={value}
                onChange={(e) => {
                  onChange(e.target.checked);
                }}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <CSSTransition
            in={watch("differentAddress")}
            timeout={{
              enter: 1000,
              exit: 300,
            }}
            mountOnEnter
            unmountOnExit
            nodeRef={differentAddressNodeRef}
          >
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="address"
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      id="address-order-from-contract"
                      label={t("orders:orderAddress")}
                      withAsterisk
                      disabled={!watch("differentAddress")}
                      value={value}
                      onChange={onChange}
                      error={errors.address ? t(`validation:${errors.address.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="countryId"
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry
                      id="country-order-from-contract"
                      label={t("common:country")}
                      withAsterisk
                      value={value}
                      onChange={(e: any) => {
                        onChange(e);
                      }}
                      valueKey={"id"}
                      labelKey={"name"}
                      error={errors.countryId ? t(`validation:${errors.countryId.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name="postCode"
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      id="postCode-order-from-contract"
                      label={t("orders:postCode")}
                      withAsterisk
                      disabled={!watch("differentAddress")}
                      value={value}
                      onChange={onChange}
                      error={errors.postCode ? t(`validation:${errors.postCode.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name="place"
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      id="postPlace"
                      label={t("orders:place")}
                      withAsterisk
                      disabled={!watch("differentAddress")}
                      value={value}
                      onChange={onChange}
                      error={errors.place ? t(`validation:${errors.place.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Divider />
              </Grid.Col>
            </Grid>
          </CSSTransition>
        </Grid.Col>

        <Grid.Col span={12}>
          <Controller
            control={control}
            name="description"
            render={({ field: { onChange, value } }) => (
              <TextInput label={t("common:description")} value={value ?? ""} onChange={onChange} />
            )}
          />
        </Grid.Col>
        {/* <Grid.Col span={12}>
          <Select label={t("common:companyToTradeWith")} />
        </Grid.Col> */}
      </Grid>
    </form>
  );
}

export default Delivery;
